// ✅ src/pages/VehicleV2/VehicleMainTypeReport.jsx
// UPDATED FULL FILE
//
// Uses backend:
//   GET /api/ownVehicleMasterReport/list?branchId=&branchType=&branch=&vehicleType=&page=&pageSize=
//
// Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + listLoading
// - prevents repeated initial API calls
// - safer view all / print flow
// - stable totals computation
// - print-friendly report layout

import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";

// ✅ backend route
const API_BASE = "http://nissi-india.com/api/ownVehicleMasterReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toNum = (v, d = 0) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNum(value, 0));

const defaultFiltersByRole = (branchType, branchId) =>
  toStr(branchType).toUpperCase() !== "HO"
    ? { branch: String(branchId), vehicleType: "Select" }
    : { branch: "Select", vehicleType: "Select" };

const mapRow = (r, idx) => ({
  sno: r.sno ?? idx + 1,
  name:
    r.name ??
    r.type ??
    r.VehicleType ??
    r.fld_vehicletypename ??
    r.VehicleTypeName ??
    r.fld_vehicletype ??
    r.fld_vehicle_subtypename ??
    "",
  qty: toNum(r.qty ?? r.Qty ?? r.count ?? r.vehicleCount ?? r.typetotal ?? 0, 0),
  total:
    r.total ??
    r.value ??
    r.sum1 ??
    r.Total ??
    r.totalPurchaseValue ??
    r.purchaseValue ??
    r.TotalPurchaseValue ??
    r.fld_totalpurchasevalue ??
    "0.00",
});

const VehicleMainTypeReport = ({
  branchType: propBranchType,
  branchId: propBranchId,
  loginUserId: propLoginUserId,
  vehicleType: propVehicleType,
  branch: propBranch,
}) => {
  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    propBranchId ??
    localStorage.getItem("branchId") ??
    localStorage.getItem("Branchid") ??
    localStorage.getItem("branchid") ??
    "8";

  const branchType =
    propBranchType ??
    localStorage.getItem("branchType") ??
    localStorage.getItem("branchtype") ??
    "HO";

  const userId =
    propLoginUserId ??
    sessionStorage.getItem("loginUserId") ??
    localStorage.getItem("userId") ??
    localStorage.getItem("loginuserid") ??
    "0";

  const sessionBranchId = toInt(branchIdRaw, 8);
  const sessionLoginUserId = toInt(userId, 0);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(sessionLoginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, sessionLoginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: sessionLoginUserId,
    }),
    [sessionBranchId, branchType, sessionLoginUserId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     state
  =========================== */
  const initialFilters = useMemo(() => {
    const base = defaultFiltersByRole(session.branchType, session.branchId);

    return {
      branch:
        propBranch !== undefined && propBranch !== null && String(propBranch).trim() !== ""
          ? String(propBranch)
          : base.branch,
      vehicleType:
        propVehicleType !== undefined &&
        propVehicleType !== null &&
        String(propVehicleType).trim() !== ""
          ? String(propVehicleType)
          : base.vehicleType,
    };
  }, [session.branchType, session.branchId, propBranch, propVehicleType]);

  const [filters, setFilters] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const isBusy = pageLoading || listLoading;

  /* ===========================
     API param builder
  =========================== */
  const buildParams = useCallback(
    (f) => {
      const finalBranch =
        toStr(session.branchType).toUpperCase() !== "HO"
          ? String(session.branchId)
          : isSelect(f.branch)
          ? "Select"
          : String(f.branch);

      const finalVehicleType = isSelect(f.vehicleType)
        ? "Select"
        : String(f.vehicleType);

      return {
        branchId: String(session.branchId),
        branchType: String(session.branchType),
        loginUserId: String(session.loginUserId),
        branch: finalBranch,
        vehicleType: finalVehicleType,
        page: 1,
        pageSize: 100000,
      };
    },
    [session]
  );

  /* ===========================
     list load
  =========================== */
  const loadList = useCallback(
    async (nextFilters = filters) => {
      try {
        setListLoading(true);
        setErrMsg("");

        const params = buildParams(nextFilters);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          setErrMsg(data?.message || "Failed to load report");
          return;
        }

        const list = data?.rows || data?.data || data?.recordset || [];
        const normalized = list.map(mapRow);

        setRows(normalized);
      } catch (e) {
        console.error(e);
        setRows([]);
        setErrMsg(e?.response?.data?.message || e.message || "Server error");
      } finally {
        setListLoading(false);
      }
    },
    [filters, buildParams, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      await loadList(initialFilters);
    } finally {
      setPageLoading(false);
    }
  }, [loadList, initialFilters]);

  /* ===========================
     handlers
  =========================== */
  const handleViewAll = useCallback(async () => {
    const reset = defaultFiltersByRole(session.branchType, session.branchId);
    setFilters(reset);
    await loadList(reset);
  }, [session.branchType, session.branchId, loadList]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  /* ===========================
     totals
  =========================== */
  const totalQty = useMemo(
    () => rows.reduce((sum, r) => sum + toNum(r.qty, 0), 0),
    [rows]
  );

  const totalValue = useMemo(
    () => rows.reduce((sum, r) => sum + toNum(r.total, 0), 0),
    [rows]
  );

  const currentDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <div className="max-w-4xl mx-auto bg-white print:max-w-none print:mx-0">
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            padding: 20px;
            font-size: 12pt;
          }
          .no-print {
            display: none !important;
          }
          table {
            border-collapse: collapse;
          }
          th,
          td {
            border: 1px solid #000;
            padding: 6px;
          }
        }
      `}</style>

      <div className="p-8 print:p-4">
        {/* Header */}
        <div className="border-2 border-gray-800 mb-6 print:border-black">
          <div className="flex items-center p-4 print:p-3">
            <div className="w-20 h-20 border border-gray-400 flex items-center justify-center mr-6 print:w-16 print:h-16 print:mr-4">
              <span className="text-xs text-gray-500 font-semibold">LOGO</span>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-2xl font-bold text-gray-800 mb-1 print:text-xl">
                NISSI INFOTECH
              </h1>
              <h2 className="text-lg font-semibold text-gray-700 mb-2 print:text-base">
                Nissi Civil Construction
              </h2>
              <div className="text-sm text-gray-600 space-y-1 print:text-xs">
                <p>3/42-12, Nagappa Colony, Kavundampalayam - 641030</p>
                <p>Tamil Nadu, India</p>
                <p className="font-medium">Phone: +91 9003744644</p>
                <p className="text-xs">infracnsoft-implement</p>
              </div>
            </div>

            <div className="text-right text-sm print:text-xs">
              <p className="font-semibold">Report Date:</p>
              <p>{currentDate}</p>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-4">
          <h3 className="text-xl font-bold text-gray-800 underline decoration-2 underline-offset-4 print:text-lg">
            VEHICLE MAIN TYPE REPORT
          </h3>
        </div>

        {/* Meta */}
        <div className="mb-6 text-sm text-gray-700 space-y-1">
          <p>
            <span className="font-semibold">Branch Type:</span> {session.branchType}
          </p>
          <p>
            <span className="font-semibold">Branch:</span>{" "}
            {isSelect(filters.branch) ? "ALL" : filters.branch}
          </p>
          <p>
            <span className="font-semibold">Vehicle Type:</span>{" "}
            {isSelect(filters.vehicleType) ? "ALL" : filters.vehicleType}
          </p>
        </div>

        {/* Status */}
        <div className="mb-4 no-print">
          {pageLoading || listLoading ? (
            <div className="text-sm text-blue-600 font-medium">Loading report...</div>
          ) : errMsg ? (
            <div className="text-sm text-red-600 font-medium">{errMsg}</div>
          ) : (
            <div className="text-sm text-green-700 font-medium">
              Loaded {rows.length} record(s)
            </div>
          )}
        </div>

        {/* Table */}
        <div className="mb-6">
          <table className="w-full border-collapse border-2 border-gray-800 print:border-black">
            <thead>
              <tr className="bg-gray-800 text-white print:bg-gray-300 print:text-black">
                <th className="border border-gray-600 px-3 py-3 text-center font-bold print:border-black print:py-2">
                  S.No
                </th>
                <th className="border border-gray-600 px-4 py-3 text-left font-bold print:border-black print:py-2">
                  Vehicle Type Name
                </th>
                <th className="border border-gray-600 px-3 py-3 text-center font-bold print:border-black print:py-2">
                  Qty
                </th>
                <th className="border border-gray-600 px-4 py-3 text-center font-bold print:border-black print:py-2">
                  Total Purchase Value (₹)
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={`${row.name}-${idx}`}
                  className={`${
                    idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                  } hover:bg-gray-100 print:bg-white`}
                >
                  <td className="border border-gray-300 px-3 py-2 text-center font-medium print:border-black">
                    {row.sno}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 font-medium print:border-black">
                    {row.name}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-center font-medium print:border-black">
                    {row.qty}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-right font-medium print:border-black">
                    {formatCurrency(row.total)}
                  </td>
                </tr>
              ))}

              <tr className="bg-gray-200 font-bold border-t-2 border-gray-800 print:bg-gray-100 print:border-black">
                <td
                  className="border border-gray-800 px-3 py-3 text-center print:border-black"
                  colSpan="2"
                >
                  TOTAL
                </td>
                <td className="border border-gray-800 px-3 py-3 text-center print:border-black">
                  {totalQty}
                </td>
                <td className="border border-gray-800 px-4 py-3 text-right print:border-black">
                  {formatCurrency(totalValue)}
                </td>
              </tr>
            </tbody>
          </table>

          {!isBusy && rows.length === 0 && !errMsg ? (
            <div className="text-center text-sm text-gray-500 mt-3">
              No records found
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="mt-8 print:mt-6">
          <div className="flex justify-between items-end">
            <div className="text-sm print:text-xs">
              <p className="font-semibold mb-1">Report Generated By:</p>
              <p>Nissi Infotech System</p>
              <p className="text-gray-600">Automated Report</p>
            </div>

            <div className="text-right">
              <div className="border-t border-gray-400 pt-2 mt-12 w-48">
                <p className="text-sm font-semibold print:text-xs">
                  Authorized Signature
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 text-center no-print flex items-center justify-center gap-3 flex-wrap">
          <button
            onClick={handleViewAll}
            disabled={isBusy}
            className={`px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-lg transition-colors duration-200 ${
              isBusy ? "opacity-60 cursor-not-allowed" : ""
            }`}
            type="button"
          >
            View All
          </button>

          <button
            onClick={handlePrint}
            disabled={isBusy}
            className={`px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg transition-colors duration-200 ${
              isBusy ? "opacity-60 cursor-not-allowed" : ""
            }`}
            type="button"
          >
            🖨️ Print Report
          </button>
        </div>

        <div className="mt-6 text-center text-xs text-gray-500 print:text-black print:mt-4">
          <p>
            This is a computer-generated report. No signature is required for
            authenticity.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VehicleMainTypeReport;